import { Field, LinkField } from '@sitecore-jss/sitecore-jss-nextjs';

/**
 * Retrieves a shared link field from the given fields object.
 * It first checks for a localized link field, and if not found, it checks for a shared link field.
 *
 * @param fields - The fields object containing link fields.
 * @param fieldName - The name of the field to retrieve.
 * @returns The link field if found, otherwise undefined.
 */
export function getSharedLink<T extends Record<K, { value?: unknown }>, K extends keyof T>(
  fields: T,
  fieldName: K
): LinkField | undefined {
  const localizedLinkField = fields?.[fieldName] as LinkField;
  const isLocalizedLinkSpecified = !!localizedLinkField?.value?.href;
  if (isLocalizedLinkSpecified) {
    return localizedLinkField;
  }

  const sharedFieldName = `${fieldName as string}Shared` as keyof T;
  const sharedLinkField = fields?.[sharedFieldName] as LinkField;
  const isSharedLinkSpecified = !!sharedLinkField?.value?.href;
  return isSharedLinkSpecified ? sharedLinkField : undefined;
}

/**
 * Type guard to check if a field is a BooleanField.
 *
 * @param field - The field to check.
 * @returns True if the field is a BooleanField, otherwise false.
 */
export const isBooleanField = (field: Field | any): field is Field<'False' | 'True'> => {
  return (
    typeof field === 'object' &&
    field &&
    typeof field.value === 'string' &&
    ['False', 'True'].includes(field.value)
  );
};
